import { useEffect, useMemo, useState } from "react";

import type { EchoflicksSessionEvent } from "../types";
import { getDefaultAwsMediaBucket } from "../providers/aws/client";
import {
  dogCastPointerFromEvents,
  parseDogCast,
  type DogCast,
  type DogCastPointer,
} from "./dogCast";
import { useMediaUrl } from "./useMediaUrl";

export type DogCastState =
  | { status: "idle" | "loading" }
  | { status: "loaded"; cast: DogCast }
  | { status: "error"; message: string };

/**
 * Loads the dog lane's reviewable cast document from its signed URL.
 *
 * Mirrors `usePersonCast`: the key comes from the dashboard event rather than
 * a session column, because the cast is an observability artifact the pipeline
 * never reads — it deliberately has no place in the render envelope.
 */
export function useDogCast(events: EchoflicksSessionEvent[]) {
  const pointer: DogCastPointer | null = useMemo(
    () => dogCastPointerFromEvents(events),
    [events],
  );
  const castUrlState = useMediaUrl(
    pointer
      ? {
          storage_bucket: getDefaultAwsMediaBucket(),
          storage_path: pointer.castKey,
          storage_is_public: false,
        }
      : null,
  );
  const castUrl = castUrlState.url;
  const [state, setState] = useState<DogCastState>({ status: "idle" });

  useEffect(() => {
    if (!castUrl) {
      setState({ status: "idle" });
      return;
    }
    let active = true;
    setState({ status: "loading" });
    fetch(castUrl)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Cast request failed with ${response.status}`);
        }
        return await response.json();
      })
      .then((raw) => {
        if (!active) return;
        const cast = parseDogCast(raw);
        setState(
          cast
            ? { status: "loaded", cast }
            : { status: "error", message: "Unrecognised dog-cast document" },
        );
      })
      .catch((error) => {
        if (!active) return;
        setState({
          status: "error",
          message:
            error instanceof Error ? error.message : "Could not load the cast",
        });
      });
    return () => {
      active = false;
    };
  }, [castUrl]);

  return { pointer, state, castUrlState };
}
